// src/features/inventory/components/HazardDot.jsx
import React from 'react';
import { Box, Tooltip, Typography } from '@mui/material';
import { useTranslation } from 'react-i18next';

// The clinic's own traffic-light rating on a safety data sheet, safest first
export const HAZARD_LEVELS = ['green', 'yellow', 'red'];

// Fixed traffic-light colours rather than the theme's: its warning colour is
// orange and reads as red next to the red dot
export const HAZARD_COLOR = {
  green: '#2e9d4a',
  yellow: '#f2c200',
  red: '#d32f2f',
};

/** "Red · High hazard" */
export function hazardLevelText(t, level) {
  return `${t(`inventory:msds.hazard_level.${level}`)} · ${t(`inventory:msds.hazard_level.${level}_hint`)}`;
}

/**
 * A coloured dot for an MSDS hazard level, shown before the product name.
 * Renders nothing when the sheet has no level set. With `tooltip={false}` the
 * dot sits next to a visible label, so screen readers skip it.
 */
export default function HazardDot({ level, size = 10, tooltip = true, sx }) {
  const { t } = useTranslation(['inventory']);
  if (!HAZARD_COLOR[level]) return null;

  const label = `${t('inventory:msds.hazard_level.label')}: ${hazardLevelText(t, level)}`;
  const dot = (
    <Box
      component="span"
      {...(tooltip ? { role: 'img', 'aria-label': label } : { 'aria-hidden': true })}
      data-testid={`hazard-dot-${level}`}
      sx={{
        display: 'inline-block',
        width: size,
        height: size,
        borderRadius: '50%',
        bgcolor: HAZARD_COLOR[level],
        // Keeps the yellow dot visible on a white background
        boxShadow: 'inset 0 0 0 1px rgba(0, 0, 0, 0.15)',
        flexShrink: 0,
        ...sx,
      }}
    />
  );
  return tooltip ? <Tooltip title={label}>{dot}</Tooltip> : dot;
}

/** Dot, level name and a muted hint: "● Red · High hazard" */
export function HazardLevelLabel({ level }) {
  const { t } = useTranslation(['inventory']);
  return (
    <Box component="span" sx={{ display: 'inline-flex', alignItems: 'center', gap: 2 }}>
      <HazardDot level={level} tooltip={false} />
      <span>
        {t(`inventory:msds.hazard_level.${level}`)}
        <Typography component="span" variant="body2" color="text.secondary">
          {' · '}{t(`inventory:msds.hazard_level.${level}_hint`)}
        </Typography>
      </span>
    </Box>
  );
}
